/**
 * Command Center basemap — Esri World Imagery + reference labels (FH2-like hybrid).
 * Pin / AI crown / Observation logic stay outside this module.
 *
 * Day: bright satellite + road/place overlays.
 * Night: darkened satellite (ops look) + same overlays.
 */

/** Legacy OpenFreeMap URLs (group-detail / fallbacks may still import). */
export const OPENFREEMAP_STYLE_BRIGHT =
  "https://tiles.openfreemap.org/styles/bright";
export const OPENFREEMAP_STYLE_DARK = "https://tiles.openfreemap.org/styles/dark";
export const OPENFREEMAP_STYLE_POSITRON =
  "https://tiles.openfreemap.org/styles/positron";
export const OPENFREEMAP_STYLE_LIBERTY =
  "https://tiles.openfreemap.org/styles/liberty";

/** Esri ArcGIS Online XYZ (no API key for public World_* services). */
export const ESRI_WORLD_IMAGERY_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";
export const ESRI_WORLD_TRANSPORT_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}";
export const ESRI_WORLD_PLACES_TILES =
  "https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}";

const ESRI_ATTRIBUTION =
  "Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community";

/** Local hour auto theme: 06:00–17:59 day · else night. */
export function resolveAutoMapTheme(now = new Date()) {
  const h = now.getHours();
  return h >= 6 && h < 18 ? "day" : "night";
}

/**
 * Command Center night pref (satellite dark). Default ON for ops look when unset.
 * @returns {"day"|"night"}
 */
export function resolveCommandCenterMapTheme() {
  try {
    if (typeof localStorage !== "undefined") {
      const v = String(localStorage.getItem("croc_home_map_night") || "").trim();
      if (v === "0") return "day";
      if (v === "1") return "night";
    }
  } catch (_) {}
  return "night";
}

/**
 * Build MapLibre style: satellite raster + transportation + place labels.
 * @param {{ theme?: "day" | "night" | "auto" }} [opts]
 * @returns {object}
 */
export function buildCommandCenterBasemapStyle(opts) {
  const mode = opts?.theme || "auto";
  const theme =
    mode === "auto" ? resolveAutoMapTheme() : mode === "day" ? "day" : "night";
  const night = theme === "night";

  return {
    version: 8,
    name: night ? "croc-cc-sat-night" : "croc-cc-sat-day",
    sources: {
      "esri-imagery": {
        type: "raster",
        tiles: [ESRI_WORLD_IMAGERY_TILES],
        tileSize: 256,
        maxzoom: 19,
        attribution: ESRI_ATTRIBUTION,
      },
      "esri-transport": {
        type: "raster",
        tiles: [ESRI_WORLD_TRANSPORT_TILES],
        tileSize: 256,
        maxzoom: 19,
        attribution: "Esri",
      },
      "esri-places": {
        type: "raster",
        tiles: [ESRI_WORLD_PLACES_TILES],
        tileSize: 256,
        maxzoom: 19,
        attribution: "Esri",
      },
    },
    layers: [
      {
        id: "esri-imagery",
        type: "raster",
        source: "esri-imagery",
        paint: night
          ? {
              "raster-opacity": 1,
              "raster-brightness-min": 0,
              "raster-brightness-max": 0.52,
              "raster-saturation": -0.28,
              "raster-contrast": 0.12,
            }
          : {
              "raster-opacity": 1,
              "raster-brightness-min": 0,
              "raster-brightness-max": 1,
              "raster-saturation": -0.05,
              "raster-contrast": 0.05,
            },
      },
      {
        id: "esri-transport",
        type: "raster",
        source: "esri-transport",
        paint: {
          "raster-opacity": night ? 0.72 : 0.85,
        },
      },
      {
        id: "esri-places",
        type: "raster",
        source: "esri-places",
        paint: {
          "raster-opacity": night ? 0.9 : 0.95,
        },
      },
    ],
  };
}

/**
 * @param {{ theme?: "day" | "night" | "auto" }} [opts]
 * @returns {string} legacy URL — prefer {@link buildCommandCenterBasemapStyle} for CC
 */
export function resolveDashboardMapStyle(opts) {
  const mode = opts?.theme || "auto";
  const theme = mode === "auto" ? resolveAutoMapTheme() : mode;
  return theme === "night" ? OPENFREEMAP_STYLE_DARK : OPENFREEMAP_STYLE_BRIGHT;
}

/** MapLibre viewport light — building shadow when vector buildings exist. */
export const MAP_LIGHT_DAY = {
  anchor: "viewport",
  color: "#ffffff",
  intensity: 0.5,
  position: [1.15, 210, 30],
};

export const MAP_LIGHT_NIGHT = {
  anchor: "viewport",
  color: "#8899bb",
  intensity: 0.35,
  position: [1.15, 210, 30],
};

/**
 * OSM extrusion height — literal meters only.
 * OpenFreeMap tiles often carry JSON-null height/levels; any ``to-number`` /
 * ``get`` chain still races the style's own fill-extrusion layers. Constant
 * height keeps our sentinel layer worker-safe after style extrusions are stripped.
 */
export const BUILDING_EXTRUSION_HEIGHT_EXPR = 12;

/** 3D camera — oblique (NOT 90° top-down). Reference pitch 60–70. */
export const DASHBOARD_MAP_DEFAULTS = {
  pitch3d: 65,
  bearing3d: -17,
  zoom: 17,
  minZoom: 14,
  maxZoom: 19,
  maxPitch: 70,
};
